import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useAnimationFrame, useMotionValueEvent } from 'framer-motion';
import { Star } from 'lucide-react';
type Review = {
  name: string;
  meta: string;
  quote: string;
};
const reviews: Review[] = [{
  name: 'Martín G.',
  meta: 'Viajó con Buceo y Navegación',
  quote: 'La organización fue impecable de principio a fin. Mauro siempre estuvo atento a cada detalle y eso nos permitió disfrutar el viaje con total tranquilidad.'
}, {
  name: 'Laura S.',
  meta: 'Viajó con Buceo y Navegación',
  quote: 'Lo que más valoro es viajar con personas que realmente conocen los destinos. Siempre hubo alguien para ayudar, recomendar y resolver cualquier situación.'
}, {
  name: 'Diego F.',
  meta: 'Viajó con Buceo y Navegación',
  quote: 'Mauro transmite confianza desde el primer momento. Su experiencia y conocimiento hacen que todo el viaje se disfrute mucho más.'
}, {
  name: 'Sofía M.',
  meta: 'Viajó con Buceo y Navegación',
  quote: 'Viajar con Buceo y Navegación fue una experiencia excelente. Profesionalismo, buena organización y un grupo humano increíble.'
}, {
  name: 'Andrés P.',
  meta: 'Viajó con Buceo y Navegación',
  quote: 'Cada detalle estuvo cuidado. Desde la planificación previa hasta las actividades durante el viaje. Sin dudas volvería a viajar con ellos.'
}];
function Stars() {
  return <div className="flex items-center gap-1 mb-4" aria-label="5 de 5 estrellas" data-id="element-456" data-mp="zyivpfhq0nzi">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className="w-4 h-4 text-noronha-gold fill-noronha-gold" aria-hidden="true" data-id="element-457" data-mp="q7ej2mfipf7u" data-mpi={i} />)}
    </div>;
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  return <div className="shrink-0 w-[300px] sm:w-[360px] md:w-[400px] bg-white rounded-3xl border border-slate-100 shadow-[0_12px_30px_rgba(2,6,23,0.08)] p-6 md:p-7 select-none" data-id="element-458" data-mp="1iuiimbvud1c8">
      <Stars data-id="element-459" data-mp="1xi93pc4qjjel" />
      <div className="mb-3" data-id="element-460" data-mp="1xgpl3evlww9n">
        <p className="font-heading text-lg md:text-xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-461" data-mp="1xfwlqyyz991j">
          {review.name}
        </p>
        <p className="text-slate-400 text-xs md:text-sm mt-0.5" data-id="element-462" data-mp="1ogsz5l0lqbia">
          {review.meta}
        </p>
      </div>
      <p className="text-slate-600 text-sm md:text-base italic leading-relaxed" data-id="element-463" data-mp="1jh6dqoamdoye">
        “{review.quote}”
      </p>
    </div>;
}
const SPEED = 40; // pixels per second, slow & elegant
export function Testimonials() {
  const x = useMotionValue(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const halfWidthRef = useRef(0);
  const [paused, setPaused] = useState(false);
  const dragging = useRef(false);
  const lastPointerX = useRef(0);
  // Keep x within [-halfWidth, 0] for a seamless loop (two copies rendered)
  const wrap = (value: number) => {
    const half = halfWidthRef.current;
    if (half <= 0) return value;
    let v = value;
    while (v <= -half) v += half;
    while (v > 0) v -= half;
    return v;
  };
  useMotionValueEvent(x, 'change', () => {
    if (trackRef.current && halfWidthRef.current === 0) {
      halfWidthRef.current = trackRef.current.scrollWidth / 2;
    }
  });
  useAnimationFrame((_, delta) => {
    if (trackRef.current && halfWidthRef.current === 0) {
      halfWidthRef.current = trackRef.current.scrollWidth / 2;
    }
    if (paused || dragging.current) return;
    const move = SPEED * delta / 1000;
    x.set(wrap(x.get() - move));
  });
  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    lastPointerX.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPointerX.current;
    lastPointerX.current = e.clientX;
    x.set(wrap(x.get() + dx));
  };
  const endDrag = (e: React.PointerEvent) => {
    if (dragging.current) {
      dragging.current = false;
      try {
        ;
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {}
    }
  };
  return <section className="relative bg-white py-24 md:py-32 overflow-hidden" data-id="element-464" data-mp="1gyv9vpsfvjef">
      <div className="container mx-auto px-6" data-id="element-465" data-mp="1vn87er76dab1">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16" data-id="element-466" data-mp="1voj5cadxvnu8">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-mono text-xs md:text-sm tracking-[0.22em] uppercase text-noronha-emerald block mb-5" data-id="element-467" data-mp="1quorhnl0m332">
            Testimonios
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight text-balance mb-5" data-id="element-468" data-mp="k7jk1yq0e99p">
            Experiencias reales de nuestros viajeros
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-slate-500 text-lg md:text-xl leading-relaxed text-balance" data-id="element-469" data-mp="2m41viyoqer6z">
            Historias de personas que ya viajaron con Buceo y Navegación y
            confiaron en nosotros para vivir experiencias inolvidables.
          </motion.p>
        </div>
      </div>

      {/* Marquee track */}
      <div className="relative cursor-grab active:cursor-grabbing" style={{
      touchAction: 'pan-y'
    }} onPointerEnter={() => setPaused(true)} onPointerLeave={e => {
      setPaused(false);
      endDrag(e);
    }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} data-id="element-470" data-mp="1ci35sf36fk8">
        <motion.div ref={trackRef} style={{
        x
      }} className="flex gap-6 md:gap-8 w-max px-6" data-id="element-471" data-mp="inx1snmgmhks">
          {[...reviews, ...reviews].map((review, i) => <ReviewCard key={i} review={review} data-id="element-472" data-mp="8xlmy618mnz4" data-mpi={i} />)}
        </motion.div>

        {/* Edge fades for premium feel */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-white to-transparent" data-id="element-473" data-mp="3yr79cykhfj8" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-white to-transparent" data-id="element-474" data-mp="11gix96tgg64u" />
      </div>
    </section>;
}